import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SubTeamsPanel from '../components/teams/SubTeamsPanel';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Users, Plus, Trash2, ArrowLeft, UserPlus, UserMinus, MapPin, User, Crown, Replace, Shield, History, Loader2, Search, X, Bell, BellOff, Wrench, Stethoscope, AlertTriangle, Inbox, Pencil, Clock, MonitorSmartphone, Upload, Check } from 'lucide-react';
import KioskSetupDialog from '../components/KioskSetupDialog';
import KioskLiveView from '../components/KioskLiveView';
import ApprovalsInbox from '../components/ApprovalsInbox';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const ManageTeams = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  const { toast } = useToast();
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showAddTeamDialog, setShowAddTeamDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  // Search box inside the "Add Team Member" dialog. Companies with dozens of
  // workers shouldn't have to scroll a list to find one — typing a name or
  // email filters the candidates instantly. Cleared whenever the dialog opens.
  const [memberSearch, setMemberSearch] = useState('');
  // Multi-select add + CSV crew import + session counter (Aug 2026)
  const [selectedAddIds, setSelectedAddIds] = useState(new Set());
  const [addedCount, setAddedCount] = useState(0);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [searchHint, setSearchHint] = useState(null);   // { archived: [], non_workers: [] }
  const [importSummary, setImportSummary] = useState(null);
  const csvInputRef = useRef(null);
  const [showReplaceSupervisorDialog, setShowReplaceSupervisorDialog] = useState(false);
  // Multi-supervisor support (Feb 2026): admins can add an additional
  // supervisor to a team (or remove one) without losing the existing leads.
  const [showAddSupervisorDialog, setShowAddSupervisorDialog] = useState(false);
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [showReplaceMemberDialog, setShowReplaceMemberDialog] = useState(false);
  const [memberBeingReplaced, setMemberBeingReplaced] = useState(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTeam, setHistoryTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  // "Repair worker visibility" tool — runs both backfill endpoints so
  // jobs assigned to teams (live or deleted) get an explicit member
  // snapshot, restoring access for workers who lost it when teams
  // changed or were removed.
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  // Per-worker diagnostic + heal flow. Find out exactly why a specific
  // worker is or isn't seeing a job — handles the duplicate-account
  // case where 86 jobs are pinned to an old user_id from a previous
  // invitation.
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagEmail, setDiagEmail] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [healLoading, setHealLoading] = useState(false);
  // Role audit — surfaces "orphan supervisors" (workers stranded at
  // role=supervisor after their team was deleted) so admins can demote
  // them with one click.
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditDemoteLoading, setAuditDemoteLoading] = useState({});
  // Approvals inbox modal — gated to admin/super_user
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  // Kiosk Mode setup dialog — holds the team being configured (null = closed)
  const [kioskTeam, setKioskTeam] = useState(null);
  // Kiosk Live View — company-wide "who's on the clock" dashboard
  const [kioskLiveOpen, setKioskLiveOpen] = useState(false);
  // Edit Team dialog (Feb 2026) — admins can rename, relocate, and
  // tweak the team's `default_shift_duration` from a single modal so
  // crews assigned to short-shift projects stop racking up
  // multi-day forgotten clock-outs.
  const [showEditTeamDialog, setShowEditTeamDialog] = useState(false);
  const [editTeam, setEditTeam] = useState({
    id: '',
    name: '',
    location: '',
    default_shift_duration: '',
  });
  const [editTeamLoading, setEditTeamLoading] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    location: '',
    supervisor_id: '',
    default_shift_duration: '', // optional team-level auto-clock-out (Feb 2026)
  });

  useEffect(() => {
    const isSupervisor = user?.role === 'supervisor';
    if (!isAdmin && !isSupervisor) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators and supervisors can manage teams',
        variant: 'destructive'
      });
      navigate('/settings');
      return;
    }
    
    if (token) {
      fetchTeams();
      if (isAdmin) fetchAllUsers();
    }
  }, [token, isAdmin]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      
      // Fetch members for each team
      for (const team of response.data) {
        fetchTeamMembers(team.id);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: 'all' }
      });
      
      // Sort users alphabetically by first name
      const sortedUsers = response.data.sort((a, b) => {
        const firstNameA = (a.first_name || a.name.split(' ')[0] || '').toLowerCase();
        const firstNameB = (b.first_name || b.name.split(' ')[0] || '').toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
      
      setAllUsers(sortedUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort team members alphabetically by first name
      const sortedMembers = response.data.sort((a, b) => {
        const firstNameA = (a.first_name || a.name.split(' ')[0] || '').toLowerCase();
        const firstNameB = (b.first_name || b.name.split(' ')[0] || '').toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
      
      setTeamMembers(prev => ({ ...prev, [teamId]: sortedMembers }));
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Coerce the optional team-level shift duration before posting.
      const payload = { ...newTeam };
      if (payload.default_shift_duration === '' || Number(payload.default_shift_duration) <= 0) {
        delete payload.default_shift_duration;
      } else {
        payload.default_shift_duration = parseFloat(payload.default_shift_duration);
      }
      await axios.post(`${API}/teams/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: 'Team created successfully! Supervisor has been notified.',
      });
      
      setShowAddTeamDialog(false);
      setNewTeam({ name: '', location: '', supervisor_id: '', default_shift_duration: '' });
      fetchTeams();
      fetchAllUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create team',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Open the Edit Team dialog seeded from the row the admin clicked.
  // `default_shift_duration` may be null on legacy teams, so we show an
  // empty string (which the form treats as "inherit member defaults").
  const openEditTeam = (team) => {
    setEditTeam({
      id: team.id,
      name: team.name || '',
      location: team.location || '',
      default_shift_duration:
        team.default_shift_duration === null || team.default_shift_duration === undefined
          ? ''
          : team.default_shift_duration,
    });
    setShowEditTeamDialog(true);
  };

  const handleEditTeam = async (e) => {
    e.preventDefault();
    if (!editTeam.id) return;
    setEditTeamLoading(true);
    try {
      const payload = {
        name: editTeam.name,
        location: editTeam.location,
      };
      // Empty / 0 clears the override → falls back to per-user shift.
      if (editTeam.default_shift_duration === '' || Number(editTeam.default_shift_duration) <= 0) {
        payload.default_shift_duration = 0;
      } else {
        payload.default_shift_duration = parseFloat(editTeam.default_shift_duration);
      }
      await axios.put(`${API}/teams/${editTeam.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: 'Team updated',
        description: 'Team settings saved successfully.',
      });
      setShowEditTeamDialog(false);
      fetchTeams();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to update team',
        variant: 'destructive',
      });
    } finally {
      setEditTeamLoading(false);
    }
  };

  // "Repair worker visibility" — calls both backfill endpoints so every
  // team-assigned job has an explicit `assigned_team_member_ids`
  // snapshot. Restores access for workers whose teams were edited /
  // deleted before the Jun 2026 snapshot fix.
  const handleRepairVisibility = async () => {
    setRepairLoading(true);
    setRepairResult(null);
    try {
      const [live, deleted] = await Promise.all([
        axios.post(`${API}/admin/backfill-team-job-snapshots`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.post(`${API}/admin/backfill-deleted-team-jobs`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const result = {
        live: live.data,
        deleted: deleted.data,
        total_repaired: (live.data.repaired_jobs || 0) + (deleted.data.repaired_jobs || 0),
      };
      setRepairResult(result);
      toast({
        title: 'Repair complete',
        description: result.total_repaired === 0
          ? 'Nothing to repair — every team-assigned job already has worker visibility set correctly.'
          : `Restored worker visibility on ${result.total_repaired} job(s).`,
      });
    } catch (error) {
      toast({
        title: 'Repair failed',
        description: error.response?.data?.detail || 'Could not run the visibility repair.',
        variant: 'destructive',
      });
    } finally {
      setRepairLoading(false);
    }
  };

  // Diagnose: given a worker email, return a full breakdown of where
  // their jobs live and why some may be hidden from their app view.
  const handleDiagnose = async (e) => {
    e?.preventDefault?.();
    if (!diagEmail.trim()) return;
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const r = await axios.get(`${API}/admin/diagnose-worker-jobs`, {
        params: { email: diagEmail.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiagResult(r.data);
    } catch (error) {
      toast({
        title: 'Diagnose failed',
        description: error.response?.data?.detail || 'Could not run diagnosis.',
        variant: 'destructive',
      });
    } finally {
      setDiagLoading(false);
    }
  };

  // Heal orphans (dry-run first, then real). Re-points active jobs from
  // any duplicate user records onto the canonical user_id.
  const handleHeal = async (dryRun) => {
    if (!diagResult?.canonical_user?.id) return;
    setHealLoading(true);
    try {
      const r = await axios.post(`${API}/admin/heal-orphan-assignments`, {
        canonical_user_id: diagResult.canonical_user.id,
        dry_run: dryRun,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (dryRun) {
        toast({
          title: 'Dry run complete',
          description: `Would re-point ${r.data.would_repoint} job(s) onto ${diagResult.canonical_user.name}.`,
        });
      } else {
        toast({
          title: 'Healed',
          description: `Re-pointed ${r.data.repointed} job(s) onto ${diagResult.canonical_user.name}.`,
        });
        // Refresh the diagnosis to show new numbers
        await handleDiagnose();
      }
    } catch (error) {
      toast({
        title: 'Heal failed',
        description: error.response?.data?.detail || 'Could not heal orphan assignments.',
        variant: 'destructive',
      });
    } finally {
      setHealLoading(false);
    }
  };

  // Role audit — fetch the categorised drift list.
  const fetchRoleAudit = async () => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/admin/role-audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuditData(r.data);
    } catch (error) {
      toast({
        title: 'Audit failed',
        description: error.response?.data?.detail || 'Could not load role audit.',
        variant: 'destructive',
      });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleOpenAudit = () => {
    setAuditOpen(true);
    fetchRoleAudit();
  };

  const handleAuditDemote = async (userId, displayName) => {
    setAuditDemoteLoading((s) => ({ ...s, [userId]: true }));
    try {
      const r = await axios.post(`${API}/admin/role-audit/demote/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: 'Demoted',
        description: r.data.message || `Demoted ${displayName}`,
      });
      // Refresh the audit list so the demoted user disappears
      await fetchRoleAudit();
    } catch (error) {
      toast({
        title: 'Demote failed',
        description: error.response?.data?.detail || 'Could not demote.',
        variant: 'destructive',
      });
    } finally {
      setAuditDemoteLoading((s) => ({ ...s, [userId]: false }));
    }
  };

  const toggleAddSelect = (userId) => {
    setSelectedAddIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleAddSelected = async () => {
    if (!selectedTeam || selectedAddIds.size === 0) return;
    setBulkAdding(true);
    try {
      const res = await axios.post(
        `${API}/teams/${selectedTeam.id}/add-members`,
        { user_ids: [...selectedAddIds] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = res.data.total_added || 0;
      setAddedCount((c) => c + n);
      setSelectedAddIds(new Set());
      setMemberSearch('');
      toast({
        title: 'Success',
        description: `${n} member${n === 1 ? '' : 's'} added to ${selectedTeam.name}`,
      });
      fetchTeamMembers(selectedTeam.id);
      fetchAllUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add members',
        variant: 'destructive'
      });
    } finally {
      setBulkAdding(false);
    }
  };

  const handleCsvFile = async (file) => {
    if (!file || !selectedTeam) return;
    const text = await file.text();
    const emails = [...new Set((text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/g) || []).map((e) => e.toLowerCase()))];
    if (emails.length === 0) {
      toast({ title: 'No emails found', description: 'The file must contain worker email addresses (CSV or plain text).', variant: 'destructive' });
      return;
    }
    setBulkAdding(true);
    setImportSummary(null);
    try {
      const res = await axios.post(
        `${API}/teams/${selectedTeam.id}/import-members`,
        { emails },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportSummary(res.data);
      setAddedCount((c) => c + (res.data.total_added || 0));
      fetchTeamMembers(selectedTeam.id);
      fetchAllUsers();
    } catch (error) {
      toast({
        title: 'Import failed',
        description: error.response?.data?.detail || 'Could not import the email list',
        variant: 'destructive'
      });
    } finally {
      setBulkAdding(false);
    }
  };

  // Archive/role hint (Aug 2026): when a search finds no addable worker,
  // explain why — the person may be archived or a supervisor/admin.
  useEffect(() => {
    if (!showAddMemberDialog) { setSearchHint(null); return undefined; }
    const term = memberSearch.trim();
    if (term.length < 2) { setSearchHint(null); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/teams/member-search-hint`, {
          params: { q: term },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSearchHint(res.data);
      } catch {
        setSearchHint(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [memberSearch, showAddMemberDialog, token]);

  const handleRemoveMember = async (teamId, userId, isSupervisor = false) => {
    const confirmMsg = isSupervisor
      ? 'WARNING: This user is the team\'s supervisor. Removing them will leave the team WITHOUT a supervisor until you assign a new one. Are you sure you want to continue?\n\nTip: Use "Change Supervisor" to swap leaders without leaving the seat empty.'
      : 'Are you sure you want to remove this member from the team?';
    if (!window.confirm(confirmMsg)) return;
    
    setIsLoading(true);
    try {
      const response = await axios.delete(`${API}/teams/${teamId}/remove-member/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: response.data?.was_supervisor
          ? 'Supervisor removed. Team has no supervisor — please assign a new one.'
          : 'Member removed from team',
      });
      
      fetchTeamMembers(teamId);
      fetchTeams();
      fetchAllUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to remove member',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplaceSupervisor = async (newSupervisorId) => {
    if (!selectedTeam || !newSupervisorId) return;

    setIsLoading(true);
    try {
      await axios.put(
        `${API}/teams/${selectedTeam.id}/supervisor?new_supervisor_id=${newSupervisorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Success',
        description: 'Team supervisor replaced. The new supervisor has been notified.',
      });

      setShowReplaceSupervisorDialog(false);
      setSelectedTeam(null);
      fetchTeams();
      fetchAllUsers();
      Object.keys(teamMembers).forEach(tid => fetchTeamMembers(tid));
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to replace supervisor',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplaceMember = async (newUserId) => {
    if (!selectedTeam || !memberBeingReplaced || !newUserId) return;

    setIsLoading(true);
    try {
      await axios.put(
        `${API}/teams/${selectedTeam.id}/replace-member?old_user_id=${memberBeingReplaced.id}&new_user_id=${newUserId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Success',
        description: 'Team member replaced successfully',
      });

      setShowReplaceMemberDialog(false);
      setMemberBeingReplaced(null);
      fetchTeams();
      fetchAllUsers();
      fetchTeamMembers(selectedTeam.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.message || 'Failed to replace member',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeamHistory = async (team) => {
    setHistoryTeam(team);
    setShowHistoryDialog(true);
    setHistoryLoading(true);
    setHistoryEvents([]);
    try {
      const response = await axios.get(`${API}/teams/${team.id}/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryEvents(response.data.events || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load team history',
        variant: 'destructive'
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? All members will be removed from the team.')) return;
    
    setIsLoading(true);
    try {
      await axios.delete(`${API}/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: 'Team deleted successfully',
      });
      
      fetchTeams();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete team',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSupervisorName = (supervisorId) => {
    const supervisor = allUsers.find(u => u.id === supervisorId);
    return supervisor ? supervisor.name : 'Unknown';
  };

  // Add an additional supervisor to a team (Feb 2026). Backend ensures the
  // user becomes role=supervisor and binds to this team. Idempotent if the
  // user is already a supervisor on this team.
  const handleAddSupervisor = async (userId) => {
    if (!selectedTeam) return;
    setIsLoading(true);
    try {
      await axios.post(
        `${API}/teams/${selectedTeam.id}/supervisors`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Supervisor added' });
      setShowAddSupervisorDialog(false);
      setSupervisorSearch('');
      await fetchTeams();
      await fetchAllUsers();
    } catch (e) {
      toast({
        title: 'Could not add supervisor',
        description: e.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Remove ONE supervisor from a team. Backend refuses to remove the last
  // supervisor when the team still has members, so we surface that as a
  // toast instead of corrupting state.
  const handleRemoveSupervisor = async (team, supervisorId) => {
    const name = getSupervisorName(supervisorId);
    if (!window.confirm(`Remove ${name} as a supervisor of ${team.name}? They will be demoted to a regular user and detached from the team.`)) {
      return;
    }
    setIsLoading(true);
    try {
      await axios.delete(
        `${API}/teams/${team.id}/supervisors/${supervisorId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Supervisor removed', description: `${name} removed from ${team.name}.` });
      await fetchTeams();
      await fetchAllUsers();
    } catch (e) {
      toast({
        title: 'Could not remove supervisor',
        description: e.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Users eligible to be ADDED as an additional supervisor: not already a
  // supervisor of this team, and not bound to a different team.
  const getAvailableUsersForSupervisor = (team) => {
    const currentSupIds = new Set(team?.supervisor_ids || (team?.supervisor_id ? [team.supervisor_id] : []));
    return allUsers.filter((u) => !currentSupIds.has(u.id) && (!u.team_id || u.team_id === team?.id));
  };

  // Toggle whether a specific supervisor on a team receives job-activity
  // notifications. Useful for large teams with several leads — only the
  // "active dispatcher" gets paged; the rest stay on the team for access
  // and visibility without the alert noise. (Feb 2026.)
  const handleToggleSupervisorNotifications = async (team, supervisorId, currentlyEnabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/teams/${team.id}/supervisors/${supervisorId}/notifications`,
        { enabled: !currentlyEnabled },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: !currentlyEnabled ? 'Notifications enabled' : 'Notifications muted',
        description: `${getSupervisorName(supervisorId)} ${!currentlyEnabled ? 'will now receive' : 'will no longer receive'} job activity notifications for ${team.name}.`,
      });
      await fetchTeams();
    } catch (e) {
      toast({
        title: 'Could not update notifications',
        description: e.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const getAvailableUsersForTeam = (teamId) => {
    // Aug 2026: workers already on ANOTHER team are still listed (adding them
    // moves them — the backend simply re-points user.team_id). Previously they
    // were hidden entirely, which made staff "disappear" from this picker.
    const teamMemberIds = teamMembers[teamId]?.map(m => m.id) || [];
    return allUsers.filter(u =>
      !teamMemberIds.includes(u.id) &&
      u.role === 'user' &&
      u.team_id !== teamId
    );
  };

  const getAvailableSupervisors = () => {
    // Allow admins, supervisors, and regular users to be team leaders
    return allUsers;
  };

  // Supervisors get a scoped view: only the teams they supervise, with
  // sub-team management (create smaller squads for specific tasks).
  if (!isAdmin) {
    const myTeams = teams.filter((t) => !t.parent_team_id && (
      (t.supervisor_ids || []).includes(user?.id) || t.supervisor_id === user?.id || t.id === user?.team_id
    ));
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="supervisor-subteams-view">
          <div>
            <h1 className="text-2xl font-bold text-white">My Teams · Sub-teams</h1>
            <p className="text-sm text-slate-400 mt-1">
              Create smaller squads within your team for specific tasks. Sub-teams can be assigned jobs just like regular teams — members stay on your main team.
            </p>
          </div>
          {myTeams.length === 0 ? (
            <p className="text-slate-500">You don't supervise any teams yet.</p>
          ) : myTeams.map((team) => (
            <Card key={team.id} className="bg-[#1A2433] border-[#2D3748]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-white">{team.name}</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setKioskTeam(team)}
                  className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10"
                  data-testid={`supervisor-kiosk-btn-${team.id}`}
                >
                  <MonitorSmartphone className="w-4 h-4 mr-1.5" />
                  Kiosk
                </Button>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <SubTeamsPanel team={team} token={token} />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Kiosk setup — supervisors can configure a clock-in tablet for their team */}
        <KioskSetupDialog
          team={kioskTeam}
          open={!!kioskTeam}
          onClose={() => setKioskTeam(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] pb-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E88E5] to-[#00C896] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/settings')}
                variant="ghost"
                className="text-white hover:bg-[#1A2433]/20 p-2"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <div>
                <div className="flex items-center space-x-3">
                  <Users className="w-8 h-8" />
                  <h1 className="text-2xl font-bold">Manage Teams</h1>
                </div>
                <p className="text-white/95 font-medium mt-1">Create teams and assign supervisors</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => setKioskLiveOpen(true)}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-medium"
                data-testid="kiosk-live-btn"
                title="Real-time view of who's on the clock across all kiosks."
              >
                <MonitorSmartphone className="w-4 h-4 mr-2" />
                Kiosk Live
              </Button>
              <Button
                onClick={() => setApprovalsOpen(true)}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-medium"
                data-testid="approvals-inbox-btn"
                title="Review role-change requests waiting on your approval."
              >
                <Inbox className="w-4 h-4 mr-2" />
                Approvals
              </Button>
              <Button
                onClick={handleOpenAudit}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-medium"
                data-testid="role-audit-btn"
                title="Find workers stranded at a role they shouldn't have (e.g. supervisor with no team) so you can fix it with one click."
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Role Audit
              </Button>
              <Button
                onClick={() => setDiagOpen(true)}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-medium"
                data-testid="diagnose-worker-btn"
                title="Look up exactly which jobs a specific worker sees vs. is assigned to, and find duplicate user records."
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                Diagnose Worker
              </Button>
              <Button
                onClick={() => setRepairOpen(true)}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-medium"
                data-testid="repair-worker-visibility-btn"
                title="Re-snapshot team membership onto every team-assigned job. Restores access for workers whose teams were edited or deleted."
              >
                <Wrench className="w-4 h-4 mr-2" />
                Repair Worker Visibility
              </Button>
              <Dialog open={showAddTeamDialog} onOpenChange={setShowAddTeamDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1A2433] text-[#1E88E5] hover:bg-[#0F1923] font-semibold">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Team
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl">Create New Team</DialogTitle>
                  <DialogDescription>
                    Create a team and assign a team leader (Admin, Supervisor, or User). The team leader will be notified via email.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTeam} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">Team Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Field Operations Team"
                      value={newTeam.name}
                      onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Team Location *</Label>
                    <Input
                      id="location"
                      placeholder="e.g., Calgary, AB or North Region"
                      value={newTeam.location}
                      onChange={(e) => setNewTeam({ ...newTeam, location: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supervisor">Assign Team Leader *</Label>
                    <Select
                      value={newTeam.supervisor_id}
                      onValueChange={(value) => setNewTeam({ ...newTeam, supervisor_id: value })}
                      required
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select team leader" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableSupervisors().map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} ({u.role}) - {u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Regular users will be promoted to Supervisor. Admins and Supervisors can also lead teams.
                    </p>
                  </div>

                  {/* Team-level default shift duration (Feb 2026). Optional —
                      when set, overrides each member's personal
                      `shift_duration` for auto-clock-out purposes. Use
                      case: assign a 4-hour project crew to a job without
                      touching individual user profiles. */}
                  <div className="space-y-2">
                    <Label htmlFor="team_shift">Default shift hours (optional)</Label>
                    <div className="flex gap-2">
                      <Select
                        value={newTeam.default_shift_duration === ''
                          ? 'inherit'
                          : ['2','4','6','8','10','12'].includes(String(newTeam.default_shift_duration))
                            ? String(newTeam.default_shift_duration)
                            : 'custom'}
                        onValueChange={(value) => {
                          if (value === 'inherit') {
                            setNewTeam({ ...newTeam, default_shift_duration: '' });
                          } else if (value !== 'custom') {
                            setNewTeam({ ...newTeam, default_shift_duration: parseFloat(value) });
                          }
                        }}
                      >
                        <SelectTrigger className="h-11 w-44" data-testid="team-shift-preset">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inherit">Use member defaults</SelectItem>
                          <SelectItem value="2">2 Hours (½ day)</SelectItem>
                          <SelectItem value="4">4 Hours (½ shift)</SelectItem>
                          <SelectItem value="6">6 Hours</SelectItem>
                          <SelectItem value="8">8 Hours (full)</SelectItem>
                          <SelectItem value="10">10 Hours</SelectItem>
                          <SelectItem value="12">12 Hours</SelectItem>
                          <SelectItem value="custom">Custom…</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="team_shift"
                        type="number"
                        min="0.25"
                        max="24"
                        step="0.25"
                        placeholder="e.g. 4"
                        value={newTeam.default_shift_duration}
                        onChange={(e) => setNewTeam({ ...newTeam, default_shift_duration: e.target.value })}
                        className="h-11 w-24"
                        data-testid="team-shift-custom"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Overrides each member&apos;s personal shift duration when set. Leave blank to use the per-user defaults.
                    </p>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 bg-[#1E88E5] hover:bg-blue-700 h-11"
                    >
                      {isLoading ? 'Creating Team...' : 'Create Team & Notify Supervisor'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowAddTeamDialog(false)}
                      variant="outline"
                      className="flex-1 h-11"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

              {/* Edit Team Dialog (Feb 2026) — mirrors create form
                  fields and exposes the team-level default shift
                  duration so admins can flip a crew between full,
                  half, and custom shifts in one place. */}
              <Dialog open={showEditTeamDialog} onOpenChange={setShowEditTeamDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Edit Team</DialogTitle>
                    <DialogDescription>
                      Update the team&apos;s name, location, and default shift duration.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleEditTeam} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="edit_team_name">Team Name *</Label>
                      <Input
                        id="edit_team_name"
                        value={editTeam.name}
                        onChange={(e) => setEditTeam({ ...editTeam, name: e.target.value })}
                        required
                        className="h-11"
                        data-testid="edit-team-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_team_location">Team Location *</Label>
                      <Input
                        id="edit_team_location"
                        value={editTeam.location}
                        onChange={(e) => setEditTeam({ ...editTeam, location: e.target.value })}
                        required
                        className="h-11"
                        data-testid="edit-team-location-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit_team_shift">Default shift hours (optional)</Label>
                      <div className="flex gap-2">
                        <Select
                          value={editTeam.default_shift_duration === '' || editTeam.default_shift_duration === null
                            ? 'inherit'
                            : ['2','4','6','8','10','12'].includes(String(editTeam.default_shift_duration))
                              ? String(editTeam.default_shift_duration)
                              : 'custom'}
                          onValueChange={(value) => {
                            if (value === 'inherit') {
                              setEditTeam({ ...editTeam, default_shift_duration: '' });
                            } else if (value !== 'custom') {
                              setEditTeam({ ...editTeam, default_shift_duration: parseFloat(value) });
                            }
                          }}
                        >
                          <SelectTrigger className="h-11 w-44" data-testid="edit-team-shift-preset">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">Use member defaults</SelectItem>
                            <SelectItem value="2">2 Hours (½ day)</SelectItem>
                            <SelectItem value="4">4 Hours (½ shift)</SelectItem>
                            <SelectItem value="6">6 Hours</SelectItem>
                            <SelectItem value="8">8 Hours (full)</SelectItem>
                            <SelectItem value="10">10 Hours</SelectItem>
                            <SelectItem value="12">12 Hours</SelectItem>
                            <SelectItem value="custom">Custom…</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          id="edit_team_shift"
                          type="number"
                          min="0.25"
                          max="24"
                          step="0.25"
                          placeholder="e.g. 4"
                          value={editTeam.default_shift_duration}
                          onChange={(e) => setEditTeam({ ...editTeam, default_shift_duration: e.target.value })}
                          className="h-11 w-24"
                          data-testid="edit-team-shift-custom"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Overrides each member&apos;s personal shift duration when set. Leave blank to use the per-user defaults.
                      </p>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        type="submit"
                        disabled={editTeamLoading}
                        className="flex-1 bg-[#1E88E5] hover:bg-blue-700 h-11"
                        data-testid="edit-team-save-btn"
                      >
                        {editTeamLoading ? 'Saving…' : 'Save Changes'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setShowEditTeamDialog(false)}
                        variant="outline"
                        className="flex-1 h-11"
                        data-testid="edit-team-cancel-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <Wrench className="w-6 h-6 text-blue-600" />
                      Repair Worker Visibility
                    </DialogTitle>
                    <DialogDescription className="space-y-2 pt-2">
                      <p>
                        Re-snapshots the current members of every team onto each
                        team-assigned job. Use this if a worker tells you they
                        can&apos;t see jobs after their team was renamed, deleted, or
                        their team membership changed.
                      </p>
                      <p className="text-xs">
                        Safe to run anytime — already-fixed jobs are skipped.
                      </p>
                    </DialogDescription>
                  </DialogHeader>
                  {repairResult && (
                    <div className="rounded-lg border-2 border-emerald-700/40 bg-emerald-900/20 p-4 space-y-3">
                      <div className="text-emerald-300 font-semibold" data-testid="repair-result-total">
                        Repaired {repairResult.total_repaired} job(s) total
                      </div>
                      <div className="text-sm text-emerald-100/80 space-y-1">
                        <div>
                          Live teams: {repairResult.live.repaired_jobs} jobs across{' '}
                          {repairResult.live.teams_processed} team(s)
                        </div>
                        <div>
                          Deleted teams: {repairResult.deleted.repaired_jobs} jobs across{' '}
                          {repairResult.deleted.teams_processed} team(s)
                        </div>
                      </div>
                      {(repairResult.live.details?.length || 0) +
                        (repairResult.deleted.details?.length || 0) > 0 && (
                        <div className="text-xs text-emerald-100/70 max-h-40 overflow-auto space-y-1">
                          {repairResult.live.details?.map((d) => (
                            <div key={`live-${d.team_id}`}>
                              • {d.team_name || d.team_id}: {d.repaired_jobs} job(s),{' '}
                              {d.member_count} member(s)
                            </div>
                          ))}
                          {repairResult.deleted.details?.map((d) => (
                            <div key={`gone-${d.deleted_team_id}`}>
                              • {d.team_name || '(deleted team)'}: {d.repaired_jobs} job(s)
                              reconstructed from history
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => { setRepairOpen(false); setRepairResult(null); }}
                      data-testid="repair-close-btn"
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleRepairVisibility}
                      disabled={repairLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="repair-run-btn"
                    >
                      {repairLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</>
                      ) : (
                        <><Wrench className="w-4 h-4 mr-2" />Run Repair Now</>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={diagOpen} onOpenChange={(o) => { setDiagOpen(o); if (!o) { setDiagResult(null); setDiagEmail(''); } }}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <Stethoscope className="w-6 h-6 text-blue-600" />
                      Diagnose Worker
                    </DialogTitle>
                    <DialogDescription className="pt-2">
                      Enter a worker&apos;s email to see exactly which jobs are assigned to them, which are visible in their app, and whether their account has duplicate records hiding older assignments.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDiagnose} className="flex gap-2 pt-2">
                    <Input
                      type="email"
                      placeholder="worker@yourcompany.com"
                      value={diagEmail}
                      onChange={(e) => setDiagEmail(e.target.value)}
                      className="flex-1"
                      data-testid="diagnose-email-input"
                    />
                    <Button
                      type="submit"
                      disabled={diagLoading || !diagEmail.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="diagnose-run-btn"
                    >
                      {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Diagnose'}
                    </Button>
                  </form>

                  {diagResult && (
                    <div className="space-y-4 pt-4">
                      <div className="rounded-lg border border-blue-700/40 bg-blue-900/20 p-4">
                        <div className="text-sm text-blue-200/80 mb-1">Canonical user</div>
                        <div className="font-semibold text-white" data-testid="diagnose-canonical-name">
                          {diagResult.canonical_user.name} <span className="font-normal text-blue-100/70">({diagResult.canonical_user.email})</span>
                        </div>
                        <div className="text-xs text-blue-100/70 mt-1">
                          id: <code>{diagResult.canonical_user.id}</code> · role: {diagResult.canonical_user.role}
                          {diagResult.canonical_user.is_deleted && <span className="ml-2 text-rose-300">[soft-deleted]</span>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div className="rounded-lg border border-emerald-700/40 bg-emerald-900/20 p-3">
                          <div className="text-emerald-200/70 text-xs">Visible in their Jobs page</div>
                          <div className="text-2xl font-bold text-emerald-200" data-testid="diagnose-visible-count">{diagResult.current_assignments.visible_in_jobs_page}</div>
                        </div>
                        <div className="rounded-lg border border-amber-700/40 bg-amber-900/20 p-3">
                          <div className="text-amber-200/70 text-xs">Hidden orphans (active)</div>
                          <div className="text-2xl font-bold text-amber-200" data-testid="diagnose-orphan-count">{diagResult.likely_missing_count}</div>
                        </div>
                        <div className="rounded-lg border border-slate-600/40 bg-slate-800/40 p-3">
                          <div className="text-slate-300/70 text-xs">Archived / soft-deleted</div>
                          <div className="text-2xl font-bold text-slate-200">
                            {diagResult.current_assignments.archived + diagResult.current_assignments.soft_deleted}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border-2 border-blue-700/40 bg-blue-900/10 p-4">
                        <div className="font-semibold text-blue-200 mb-1">Diagnosis</div>
                        <div className="text-sm text-blue-100/90" data-testid="diagnose-summary">{diagResult.diagnosis}</div>
                      </div>

                      {diagResult.duplicate_user_records.length > 0 && (
                        <div className="rounded-lg border border-rose-700/40 bg-rose-900/10 p-4">
                          <div className="font-semibold text-rose-200 mb-2">Duplicate user records found ({diagResult.duplicate_user_records.length})</div>
                          <div className="space-y-2">
                            {diagResult.duplicate_user_records.map((d) => (
                              <div key={d.user_id} className="text-xs text-rose-100/90 flex justify-between">
                                <div>
                                  <code className="text-rose-200">{d.user_id.slice(0, 8)}…</code> · {d.email} {d.is_deleted && <span className="text-rose-300">[deleted]</span>}
                                </div>
                                <div className="font-semibold">
                                  {d.jobs_assigned_active} active / {d.jobs_assigned_total} total
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2 pt-3 border-t border-rose-700/30 mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleHeal(true)}
                              disabled={healLoading}
                              data-testid="diagnose-heal-dryrun-btn"
                            >
                              {healLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Preview heal (dry run)'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleHeal(false)}
                              disabled={healLoading}
                              className="bg-rose-600 hover:bg-rose-700 text-white"
                              data-testid="diagnose-heal-run-btn"
                            >
                              {healLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : `Re-point ${diagResult.likely_missing_count} job(s) to canonical user`}
                            </Button>
                          </div>
                        </div>
                      )}

                      {diagResult.orphaned_sample.length > 0 && (
                        <div className="rounded-lg border border-slate-600/40 bg-slate-800/40 p-4">
                          <div className="text-sm font-semibold text-slate-200 mb-2">Sample of hidden jobs (top 10)</div>
                          <div className="space-y-1 text-xs text-slate-300/90 max-h-48 overflow-auto">
                            {diagResult.orphaned_sample.map((j) => (
                              <div key={j.id} className="flex justify-between gap-2">
                                <span className="truncate">
                                  <code className="text-slate-400">{j.job_number || j.id.slice(0,8)}</code> {j.title}
                                </span>
                                <span className="text-slate-500 shrink-0">{j.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={auditOpen} onOpenChange={(o) => { setAuditOpen(o); if (!o) setAuditData(null); }}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                      Role Audit
                    </DialogTitle>
                    <DialogDescription className="pt-2">
                      Workers stuck at a role they shouldn&apos;t have. The most common case: someone was named team-supervisor for a short-lived team and was left at <code>supervisor</code> after the team was deleted, which can hide jobs assigned to them personally.
                    </DialogDescription>
                  </DialogHeader>

                  {auditLoading && (
                    <div className="flex items-center gap-2 text-blue-300 py-4">
                      <Loader2 className="w-4 h-4 animate-spin" /> Scanning users…
                    </div>
                  )}

                  {!auditLoading && auditData && auditData.total_drift_cases === 0 && (
                    <div className="rounded-lg border-2 border-emerald-700/40 bg-emerald-900/20 p-4 text-emerald-200" data-testid="role-audit-clean">
                      No role drift detected. Every user is at the role they should be.
                    </div>
                  )}

                  {!auditLoading && auditData && auditData.orphan_supervisors.length > 0 && (
                    <div className="rounded-lg border border-amber-700/40 bg-amber-900/10 p-4 space-y-3">
                      <div className="font-semibold text-amber-200" data-testid="role-audit-orphan-count">
                        Orphan supervisors ({auditData.orphan_supervisors.length})
                      </div>
                      <div className="text-xs text-amber-100/70">
                        Role is <code>supervisor</code> but they don&apos;t supervise any team and aren&apos;t admin/super_user. Demote to USER to restore visibility on their personal job queue.
                      </div>
                      <div className="space-y-2">
                        {auditData.orphan_supervisors.map((u) => (
                          <div key={u.user_id} className="flex items-center justify-between gap-3 rounded border border-amber-700/30 bg-amber-900/20 px-3 py-2">
                            <div className="min-w-0">
                              <div className="font-medium text-white truncate" data-testid={`orphan-row-name-${u.user_id}`}>{u.name || u.email}</div>
                              <div className="text-xs text-amber-100/70 truncate">{u.email} · {u.assigned_active_jobs} active job(s)</div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleAuditDemote(u.user_id, u.name || u.email)}
                              disabled={!!auditDemoteLoading[u.user_id]}
                              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                              data-testid={`orphan-demote-btn-${u.user_id}`}
                            >
                              {auditDemoteLoading[u.user_id] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                'Demote to User'
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!auditLoading && auditData && auditData.dangling_team_supervisors.length > 0 && (
                    <div className="rounded-lg border border-rose-700/40 bg-rose-900/10 p-4 space-y-3 mt-3">
                      <div className="font-semibold text-rose-200">
                        Dangling-team supervisors ({auditData.dangling_team_supervisors.length})
                      </div>
                      <div className="text-xs text-rose-100/70">
                        These users have a <code>team_id</code> pointing at a team that no longer exists. Demote and clear.
                      </div>
                      <div className="space-y-2">
                        {auditData.dangling_team_supervisors.map((u) => (
                          <div key={u.user_id} className="flex items-center justify-between gap-3 rounded border border-rose-700/30 bg-rose-900/20 px-3 py-2">
                            <div className="min-w-0">
                              <div className="font-medium text-white truncate">{u.name || u.email}</div>
                              <div className="text-xs text-rose-100/70 truncate">{u.email} · {u.assigned_active_jobs} active job(s)</div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleAuditDemote(u.user_id, u.name || u.email)}
                              disabled={!!auditDemoteLoading[u.user_id]}
                              className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
                            >
                              {auditDemoteLoading[u.user_id] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                'Demote + Clear'
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
        </div>
      </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Team Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{teams.length}</p>
              <p className="text-sm text-[#1E88E5] font-medium">Total Teams</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">
                {Object.values(teamMembers).reduce((sum, members) => sum + members.length, 0)}
              </p>
              <p className="text-sm text-[#00C896] font-medium">Total Members</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#2D3748] bg-[#0F1923]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">
                {allUsers.filter(u => u.role === 'supervisor').length}
              </p>
              <p className="text-sm text-[#A78BFA] font-medium">Active Supervisors</p>
            </CardContent>
          </Card>
        </div>

        {/* Teams List */}
        {teams.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg mb-4">No teams yet</p>
              <Button
                onClick={() => setShowAddTeamDialog(true)}
                className="bg-[#1E88E5] hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {teams.filter((t) => !t.parent_team_id).map((team) => (
              <Card key={team.id} className="shadow-lg border-2 hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-[#1A2433] to-[#1A2433] border-b">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-2xl font-bold text-white mb-2">
                        {team.name}
                      </CardTitle>
                      <div className="flex items-center space-x-4 text-sm text-slate-400 flex-wrap gap-y-2">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4" />
                          <span>{team.location}</span>
                        </div>
                        {/* Multi-supervisor list (Feb 2026). Each supervisor
                            chip has its own remove-X button so admins can
                            curate the lead group without losing the whole
                            seat. */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Crown className="w-4 h-4 text-amber-400" />
                          <span className="text-xs uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                            Supervisor{(team.supervisor_ids || []).length === 1 ? '' : 's'}:
                          </span>
                          {(team.supervisor_ids && team.supervisor_ids.length > 0
                            ? team.supervisor_ids
                            : team.supervisor_id ? [team.supervisor_id] : []
                          ).map((sid) => {
                            const optedOut = (team.notification_opt_out_ids || []).includes(sid);
                            return (
                              <span
                                key={sid}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.25)' }}
                                data-testid={`team-supervisor-chip-${team.id}-${sid}`}
                              >
                                <span>{getSupervisorName(sid)}</span>
                                {/* Bell toggle — clicking flips this supervisor's
                                    job-activity notifications for this team only. */}
                                <button
                                  type="button"
                                  onClick={() => handleToggleSupervisorNotifications(team, sid, !optedOut)}
                                  className="opacity-70 hover:opacity-100"
                                  style={{ color: optedOut ? '#64748B' : '#F59E0B' }}
                                  aria-label={optedOut
                                    ? `Enable notifications for ${getSupervisorName(sid)}`
                                    : `Mute notifications for ${getSupervisorName(sid)}`}
                                  title={optedOut ? 'Notifications muted — click to enable' : 'Receiving notifications — click to mute'}
                                  data-testid={`toggle-supervisor-notifications-${team.id}-${sid}`}
                                >
                                  {optedOut ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSupervisor(team, sid)}
                                  className="ml-0.5 -mr-0.5 opacity-70 hover:opacity-100"
                                  aria-label={`Remove supervisor ${getSupervisorName(sid)}`}
                                  title="Remove this supervisor"
                                  data-testid={`remove-supervisor-${team.id}-${sid}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                          {(!team.supervisor_ids || team.supervisor_ids.length === 0) && !team.supervisor_id && (
                            <span className="text-xs italic" style={{ color: '#64748B' }}>none</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTeam(team);
                              setShowAddSupervisorDialog(true);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium hover:bg-amber-500/10 transition-colors"
                            style={{ color: '#F59E0B', border: '1px dashed rgba(245,158,11,0.4)' }}
                            data-testid={`add-supervisor-btn-${team.id}`}
                            title="Add another supervisor"
                          >
                            <UserPlus className="w-3 h-3" /> Add
                          </button>
                        </div>
                        <Badge className="bg-[#1A2433] text-blue-800 border-[#2D3748]">
                          {teamMembers[team.id]?.length || 0} Members
                        </Badge>
                        {team.default_shift_duration ? (
                          <Badge
                            className="bg-[#0F1923] text-amber-300 border-amber-400/30"
                            data-testid={`team-shift-badge-${team.id}`}
                            title="Team-wide auto clock-out shift duration"
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            {team.default_shift_duration}h shift
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={() => setKioskTeam(team)}
                        variant="ghost"
                        size="sm"
                        className="text-[#1E88E5] hover:bg-[#0F1923] hover:text-blue-300"
                        data-testid={`kiosk-btn-${team.id}`}
                        title="Set up a clock-in/out kiosk station for this team's location"
                      >
                        <MonitorSmartphone className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline text-xs">Kiosk</span>
                      </Button>
                      <Button
                        onClick={() => openEditTeam(team)}
                        variant="ghost"
                        size="sm"
                        className="text-slate-300 hover:bg-[#0F1923] hover:text-white"
                        data-testid={`edit-team-btn-${team.id}`}
                        title="Edit team name, location, and shift duration"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline text-xs">Edit</span>
                      </Button>
                      <Button
                        onClick={() => fetchTeamHistory(team)}
                        variant="ghost"
                        size="sm"
                        className="text-slate-300 hover:bg-[#0F1923] hover:text-white"
                        data-testid={`team-history-btn-${team.id}`}
                        title="View team history (audit log)"
                      >
                        <History className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline text-xs">History</span>
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedTeam(team);
                          setShowReplaceSupervisorDialog(true);
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-amber-400 hover:bg-[#0F1923] hover:text-amber-300"
                        data-testid={`change-supervisor-btn-${team.id}`}
                        title="Change supervisor"
                      >
                        <Crown className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline text-xs">Change Supervisor</span>
                      </Button>
                      <Button
                        onClick={() => handleDeleteTeam(team.id)}
                        variant="ghost"
                        size="sm"
                        className="text-[#EF4444] hover:bg-[#0F1923] hover:text-red-700"
                        data-testid={`delete-team-btn-${team.id}`}
                        title="Delete team"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-slate-300">Team Members</h3>
                    <Button
                      onClick={() => {
                        setSelectedTeam(team);
                        setSelectedAddIds(new Set());
                        setAddedCount(0);
                        setImportSummary(null);
                        setSearchHint(null);
                        setShowAddMemberDialog(true);
                      }}
                      size="sm"
                      className="bg-[#00C896] hover:bg-green-700"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Member
                    </Button>
                  </div>
                  
                  {!teamMembers[team.id] || teamMembers[team.id].length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No members in this team yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {teamMembers[team.id].map((member) => {
                        const isTeamSupervisor = team.supervisor_id === member.id;
                        return (
                        <div
                          key={member.id}
                          className="flex justify-between items-center p-3 bg-[#1A2433] rounded-lg border hover:border-[#2D3748] transition-colors"
                          data-testid={`team-member-row-${member.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 flex-wrap">
                              {isTeamSupervisor && (
                                <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" title="Team Supervisor" />
                              )}
                              <p className="font-medium text-white truncate">{member.name}</p>
                              {member.role === 'admin' && (
                                <Badge variant="outline" className="bg-[#0F1923] text-red-700 border-red-300 text-xs">
                                  Admin
                                </Badge>
                              )}
                              {member.role === 'supervisor' && (
                                <Badge variant="outline" className="bg-[#0F1923] text-blue-700 border-[#2D3748] text-xs">
                                  Supervisor
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-400 truncate">{member.email}</p>
                          </div>
                          <div className="flex items-center space-x-1 flex-shrink-0">
                            <Button
                              onClick={() => {
                                setSelectedTeam(team);
                                setMemberBeingReplaced(member);
                                setShowReplaceMemberDialog(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:bg-[#0F1923] hover:text-blue-300"
                              data-testid={`replace-member-btn-${member.id}`}
                              title={isTeamSupervisor ? 'Replace supervisor with another user' : 'Replace this member with another user'}
                            >
                              <Replace className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleRemoveMember(team.id, member.id, isTeamSupervisor)}
                              variant="ghost"
                              size="sm"
                              className="text-[#EF4444] hover:bg-[#0F1923]"
                              data-testid={`remove-member-btn-${member.id}`}
                              title={isTeamSupervisor ? 'Remove supervisor (leaves team without one)' : 'Remove member'}
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  <SubTeamsPanel team={team} token={token} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Team History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={(open) => {
        setShowHistoryDialog(open);
        if (!open) {
          setHistoryTeam(null);
          setHistoryEvents([]);
        }
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <History className="w-5 h-5 mr-2 text-slate-400" />
              Team History — {historyTeam?.name}
            </DialogTitle>
            <DialogDescription>
              Audit log of changes to this team. Newest events first.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="team-history-list">
            {historyLoading ? (
              <div className="text-center py-8 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading history...
              </div>
            ) : historyEvents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No history events yet for this team.</p>
            ) : (
              <ol className="relative border-l border-[#2D3748] ml-3 space-y-4">
                {historyEvents.map((ev) => {
                  const label = {
                    team_created: { text: 'Team created', color: 'text-green-400' },
                    member_added: { text: 'Member added', color: 'text-blue-400' },
                    member_removed: { text: 'Member removed', color: 'text-red-400' },
                    member_replaced: { text: 'Member replaced', color: 'text-amber-400' },
                    supervisor_replaced: { text: 'Supervisor changed', color: 'text-amber-400' },
                    supervisor_removed: { text: 'Supervisor removed', color: 'text-red-400' },
                    team_deleted: { text: 'Team deleted', color: 'text-red-400' },
                  }[ev.action] || { text: ev.action, color: 'text-slate-300' };

                  const d = ev.details || {};
                  let detailText = null;
                  if (ev.action === 'team_created') {
                    detailText = `Initial supervisor: ${d.initial_supervisor_name || d.initial_supervisor_id}`;
                  } else if (ev.action === 'member_added') {
                    detailText = `Added: ${d.member_name || d.member_id}`;
                  } else if (ev.action === 'member_removed' || ev.action === 'supervisor_removed') {
                    detailText = `Removed: ${d.member_name || d.member_id}${d.was_supervisor ? ' (was supervisor — seat is now empty)' : ''}`;
                  } else if (ev.action === 'member_replaced') {
                    detailText = `${d.previous_user_name || d.previous_user_id} → ${d.new_user_name || d.new_user_id}`;
                  } else if (ev.action === 'supervisor_replaced') {
                    const prev = d.previous_supervisor_name || d.previous_user_name || 'No previous supervisor';
                    const next = d.new_supervisor_name || d.new_user_name || d.new_supervisor_id;
                    detailText = `${prev} → ${next}`;
                  } else if (ev.action === 'team_deleted') {
                    detailText = `Team "${d.team_name}" deleted`;
                  }

                  const ts = ev.created_at ? new Date(ev.created_at).toLocaleString() : '';

                  return (
                    <li key={ev.id} className="ml-4" data-testid={`team-history-event-${ev.id}`}>
                      <div className="absolute w-3 h-3 bg-slate-500 rounded-full -left-[6.5px] mt-1.5"></div>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <p className={`font-semibold ${label.color}`}>{label.text}</p>
                        <time className="text-xs text-slate-400">{ts}</time>
                      </div>
                      {detailText && <p className="text-sm text-slate-300 mt-1">{detailText}</p>}
                      <p className="text-xs text-slate-500 mt-1">
                        by <span className="font-medium">{ev.changed_by_name}</span>
                        {ev.changed_by_email && <span> ({ev.changed_by_email})</span>}
                      </p>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Supervisor Dialog */}
      <Dialog open={showReplaceSupervisorDialog} onOpenChange={(open) => {
        setShowReplaceSupervisorDialog(open);
        if (!open) setSelectedTeam(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Crown className="w-5 h-5 mr-2 text-amber-400" />
              Change Team Supervisor
            </DialogTitle>
            <DialogDescription>
              Pick a new supervisor for <strong>{selectedTeam?.name}</strong>. The current supervisor will be demoted to a regular user and stay in the team. The new supervisor will be promoted and notified by email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allUsers
              .filter(u => u.id !== selectedTeam?.supervisor_id)
              .map((u) => (
                <Button
                  key={u.id}
                  onClick={() => handleReplaceSupervisor(u.id)}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  data-testid={`pick-new-supervisor-${u.id}`}
                >
                  <div className="text-left">
                    <p className="font-semibold flex items-center">
                      {u.name}
                      <Badge variant="outline" className="ml-2 text-xs">{u.role}</Badge>
                    </p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </div>
                </Button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Member Dialog */}
      <Dialog open={showReplaceMemberDialog} onOpenChange={(open) => {
        setShowReplaceMemberDialog(open);
        if (!open) setMemberBeingReplaced(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Replace className="w-5 h-5 mr-2 text-blue-400" />
              Replace Team Member
            </DialogTitle>
            <DialogDescription>
              Pick the user who will take <strong>{memberBeingReplaced?.name}</strong>'s spot on <strong>{selectedTeam?.name}</strong>.
              {selectedTeam && memberBeingReplaced && selectedTeam.supervisor_id === memberBeingReplaced.id && (
                <span className="block mt-2 text-amber-400">
                  This member is the team supervisor — the replacement will be promoted to supervisor.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allUsers
              .filter(u => u.id !== memberBeingReplaced?.id)
              .filter(u => !u.team_id || u.team_id === selectedTeam?.id || (selectedTeam && selectedTeam.supervisor_id === memberBeingReplaced?.id))
              .map((u) => (
                <Button
                  key={u.id}
                  onClick={() => handleReplaceMember(u.id)}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  data-testid={`pick-replacement-${u.id}`}
                >
                  <div className="text-left">
                    <p className="font-semibold flex items-center">
                      {u.name}
                      <Badge variant="outline" className="ml-2 text-xs">{u.role}</Badge>
                    </p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                    {u.team_id && u.team_id !== selectedTeam?.id && (
                      <p className="text-xs text-amber-500">Currently on another team — will be moved.</p>
                    )}
                  </div>
                </Button>
              ))}
            {allUsers.filter(u => u.id !== memberBeingReplaced?.id).length === 0 && (
              <p className="text-gray-500 text-center py-4">No available users to choose from</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Supervisor Dialog (Feb 2026 multi-supervisor) */}
      <Dialog
        open={showAddSupervisorDialog}
        onOpenChange={(o) => {
          setShowAddSupervisorDialog(o);
          if (!o) setSupervisorSearch('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Supervisor</DialogTitle>
            <DialogDescription>
              Pick a user to add as another supervisor of {selectedTeam?.name}. They will
              be promoted to the Supervisor role and bound to this team.
            </DialogDescription>
          </DialogHeader>
          {selectedTeam && getAvailableUsersForSupervisor(selectedTeam).length > 0 && (
            <div className="relative flex items-center rounded-lg border border-input bg-background">
              <Search className="w-4 h-4 ml-3 flex-shrink-0 text-gray-400" />
              <Input
                type="text"
                value={supervisorSearch}
                onChange={(e) => setSupervisorSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="border-0 bg-transparent text-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                autoFocus
                data-testid="add-supervisor-search-input"
              />
              {supervisorSearch && (
                <button
                  type="button"
                  onClick={() => setSupervisorSearch('')}
                  className="p-2 text-gray-400 hover:text-gray-700"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(() => {
              if (!selectedTeam) return null;
              const all = getAvailableUsersForSupervisor(selectedTeam);
              if (all.length === 0) {
                return <p className="text-gray-500 text-center py-4">No eligible users</p>;
              }
              const term = supervisorSearch.trim().toLowerCase();
              const filtered = term
                ? all.filter((u) =>
                    (u.name || '').toLowerCase().includes(term) ||
                    (u.email || '').toLowerCase().includes(term),
                  )
                : all;
              if (filtered.length === 0) {
                return (
                  <p className="text-gray-500 text-center py-4">
                    No users match "{supervisorSearch}".
                  </p>
                );
              }
              return filtered.map((u) => (
                <Button
                  key={u.id}
                  onClick={() => handleAddSupervisor(u.id)}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  data-testid={`add-supervisor-row-${u.id}`}
                >
                  <div className="text-left">
                    <p className="font-semibold">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email} · {u.role}</p>
                  </div>
                </Button>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog
        open={showAddMemberDialog}
        onOpenChange={(o) => {
          setShowAddMemberDialog(o);
          if (!o) setMemberSearch('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Select a user to add to {selectedTeam?.name}
            </DialogDescription>
          </DialogHeader>
          {/* Search box — filters the candidate list by name or email so
              admins don't have to scroll past dozens of users (Feb 2026). */}
          {selectedTeam && getAvailableUsersForTeam(selectedTeam.id).length > 0 && (
            <div className="relative flex items-center rounded-lg border border-input bg-background">
              <Search className="w-4 h-4 ml-3 flex-shrink-0 text-gray-400" />
              <Input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="border-0 bg-transparent text-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                autoFocus
                data-testid="add-team-member-search-input"
              />
              {memberSearch && (
                <button
                  type="button"
                  onClick={() => setMemberSearch('')}
                  className="p-2 text-gray-400 hover:text-gray-700"
                  aria-label="Clear search"
                  data-testid="add-team-member-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          {/* CSV crew import (Aug 2026) */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">Tick workers below, then Add selected — or import a CSV of emails.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => csvInputRef.current?.click()}
              disabled={bulkAdding}
              data-testid="add-team-member-import-csv-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
            </Button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleCsvFile(f); }}
              data-testid="add-team-member-csv-input"
            />
          </div>
          {importSummary && (
            <div className="rounded-lg border border-input bg-background px-3 py-2 text-xs space-y-1" data-testid="add-team-member-import-summary">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-foreground">
                  Import: {importSummary.total_added || 0} assigned
                  {importSummary.moved?.length > 0 ? ` (${importSummary.moved.length} moved from other teams)` : ''}
                </p>
                <button onClick={() => setImportSummary(null)} className="text-gray-400 hover:text-gray-600" aria-label="Dismiss import summary" data-testid="add-team-member-import-summary-close">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {importSummary.already_member?.length > 0 && (
                <p className="text-gray-500">Already on this team: {importSummary.already_member.map((x) => x.name || x.email).join(', ')}</p>
              )}
              {importSummary.archived?.length > 0 && (
                <p className="text-amber-500">Archived (restore from Staff → View Archive): {importSummary.archived.map((x) => x.name || x.email).join(', ')}</p>
              )}
              {importSummary.non_workers?.length > 0 && (
                <p className="text-blue-400">Supervisors/admins (use Add Supervisor): {importSummary.non_workers.map((x) => x.name || x.email).join(', ')}</p>
              )}
              {importSummary.not_found?.length > 0 && (
                <p className="text-red-400">No account found: {importSummary.not_found.join(', ')}</p>
              )}
            </div>
          )}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(() => {
              if (!selectedTeam) return null;
              const all = getAvailableUsersForTeam(selectedTeam.id);
              if (all.length === 0) {
                return <p className="text-gray-500 text-center py-4">No available users to add</p>;
              }
              const term = memberSearch.trim().toLowerCase();
              const filtered = term
                ? all.filter((u) =>
                    (u.name || '').toLowerCase().includes(term) ||
                    (u.email || '').toLowerCase().includes(term),
                  )
                : all;
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-4 space-y-2">
                    <p className="text-gray-500" data-testid="add-team-member-no-results">
                      No users match "{memberSearch}". Try a different name or email.
                    </p>
                    {searchHint?.archived?.length > 0 && (
                      <p className="text-xs text-amber-500 px-4" data-testid="add-team-member-archived-hint">
                        {searchHint.archived.map((a) => a.name || a.email).join(', ')} {searchHint.archived.length === 1 ? 'is' : 'are'} archived — restore from Staff → View Archive to add them.
                      </p>
                    )}
                    {searchHint?.non_workers?.length > 0 && (
                      <p className="text-xs text-blue-400 px-4" data-testid="add-team-member-role-hint">
                        {searchHint.non_workers.map((a) => `${a.name || a.email} (${a.role})`).join(', ')} — supervisors and admins aren't listed here; use "Add Supervisor" instead.
                      </p>
                    )}
                  </div>
                );
              }
              return filtered.map((u) => {
                const otherTeam = u.team_id ? teams.find((t) => t.id === u.team_id) : null;
                return (
                <Button
                  key={u.id}
                  onClick={() => toggleAddSelect(u.id)}
                  disabled={bulkAdding}
                  variant="outline"
                  className={`w-full justify-start h-auto py-3 ${selectedAddIds.has(u.id) ? 'border-[#00C896] bg-[#00C89614]' : ''}`}
                  data-testid={`add-team-member-row-${u.id}`}
                >
                  <span
                    aria-hidden="true"
                    className={`mr-3 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${selectedAddIds.has(u.id) ? 'bg-[#00C896] border-[#00C896] text-white' : 'border-input'}`}
                    data-testid={`add-team-member-check-${u.id}`}
                    data-state={selectedAddIds.has(u.id) ? 'checked' : 'unchecked'}
                  >
                    {selectedAddIds.has(u.id) && <Check className="h-3 w-3" />}
                  </span>
                  <div className="text-left">
                    <p className="font-semibold">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                    {u.team_id && (
                      <p className="text-xs text-amber-500" data-testid={`add-team-member-move-note-${u.id}`}>
                        Currently in {otherTeam?.name || 'another team'} — adding will move them here
                      </p>
                    )}
                  </div>
                </Button>
                );
              });
            })()}
          </div>
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-gray-500" data-testid="add-team-member-session-counter">
              {addedCount > 0 ? `${addedCount} added this session` : ''}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAddMemberDialog(false)}
                data-testid="add-team-member-done-btn"
              >
                Done
              </Button>
              <Button
                onClick={handleAddSelected}
                disabled={bulkAdding || selectedAddIds.size === 0}
                className="bg-[#00C896] hover:bg-green-700"
                data-testid="add-team-member-add-selected-btn"
              >
                {bulkAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${selectedAddIds.size > 0 ? selectedAddIds.size : ''} selected`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ApprovalsInbox
        open={approvalsOpen}
        onOpenChange={setApprovalsOpen}
        token={token}
        currentUserId={user?.id}
      />

      {/* Kiosk Mode setup — per-team clock-in/out station */}
      <KioskSetupDialog
        team={kioskTeam}
        open={!!kioskTeam}
        onClose={() => setKioskTeam(null)}
      />

      {/* Kiosk Live View — who's on the clock across all kiosks */}
      <KioskLiveView open={kioskLiveOpen} onClose={() => setKioskLiveOpen(false)} />
    </div>
  );
};

export default ManageTeams;
